// Every figure shown on the Technology page is declared here, once.
//
// Source: "Robustesse du watermarking invisible : benchmark face à six
// méthodes de référence", report dated 25 August 2026. 1000 COCO val2017
// images at 512×512, the same 96-bit payload for all seven methods, 14 attacks
// at one setting each. A message only counts when all 96 bits come back.

// What one image carries: 96 bits of payload inside a 256-bit carrier, the
// remaining 160 bits being the error-correcting code.
export const PAYLOAD_BITS: number = 96
export const CARRIER_BITS: number = 256

// Fidelity of the watermarked image against the original, before any attack.
export const PSNR_DB: number = 45.6
export const SSIM_PERCENT: number = 99.5

// Exact recovery averaged over the 14 attacks, and the best of the six
// reference methods on the same protocol (TrustMark-Q).
export const EXACT_RECOVERY_AVERAGE: number = 98.5
export const BEST_ALTERNATIVE_AVERAGE: number = 74.2

// Exact recovery after the automated removal chain — a remover built for
// visible text watermarks: detection, then inpainting — and the range the
// neural methods of the literature land in.
export const AI_REMOVAL_EXACT: number = 99
export const AI_REMOVAL_OTHERS: string = '2% to 62%'

export interface Attack {
	name: string
	setting: string
	// Share of the 1000 identifiers recovered without a single wrong bit.
	exact: number
}

export interface AttackFamily {
	name: string
	// Monochrome silhouette used as a mask over the brand gradient. Required
	// here, statically: a dynamic require on the images folder would make
	// webpack bundle everything under it.
	icon: string
	description: string
	attacks: Attack[]
}

// The report's five families, with neural compression folded into
// compression: on this page the point is what an image goes through, not the
// codec's architecture.
export const ATTACK_FAMILIES: AttackFamily[] = [
	{
		name: 'Compression',
		icon: require( './images/Attacks.Compression.png' ),
		description: 'Re-sharing online: recompression by platforms and messaging apps, including a next-generation neural codec.',
		attacks: [
			{ name: 'JPEG', setting: 'quality 80', exact: 100 },
			{ name: 'WebP', setting: 'quality 70', exact: 90 },
			{ name: 'AVIF', setting: 'quality 50', exact: 91 },
			{ name: 'HEIC', setting: 'quality 50', exact: 100 },
			{ name: 'Neural codec', setting: 'bmshj2018, best quality', exact: 100 },
		],
	},
	{
		name: 'Geometry',
		icon: require( './images/Attacks.Cropping.png' ),
		description: 'Screenshots, crops and republishing: the image is no longer the size or the framing it left with.',
		attacks: [
			{ name: 'Crop', setting: '10% off every edge', exact: 100 },
			{ name: 'Rotation', setting: '5°', exact: 100 },
			{ name: 'Resize', setting: 'halved, then restored', exact: 100 },
			{ name: 'Mirror', setting: 'horizontal flip', exact: 100 },
		],
	},
	{
		name: 'Noise and filtering',
		icon: require( './images/Attacks.Noise.png' ),
		description: 'Retouching and filters applied on top of the image.',
		attacks: [
			{ name: 'Gaussian noise', setting: 'σ = 0.1/255', exact: 100 },
			{ name: 'Salt and pepper', setting: '0.1% of pixels', exact: 100 },
			{ name: 'Mean filter', setting: '2×2 kernel', exact: 100 },
			{ name: 'Median filter', setting: '2×2 kernel', exact: 100 },
		],
	},
	{
		name: 'AI removal',
		icon: require( './images/Attacks.AI.png' ),
		description: 'A freely available remover built for visible text watermarks: an AI detector finds the mark, an inpainting model regenerates the area.',
		attacks: [
			{ name: 'Detection + inpainting', setting: 'Florence-2, then LaMa', exact: 99 },
		],
	},
]

// The corpus every figure on the page is measured on, and how many attacks
// each watermarked image goes through: the families' own attacks, counted.
export const IMAGE_COUNT: number = 1000
export const ATTACK_COUNT: number = ATTACK_FAMILIES.reduce(
	( count: number, family: AttackFamily ): number => count + family.attacks.length,
	0,
)

// Second series of the report: each attack is turned up until fewer than 90%
// of identifiers come back intact, on 200 phone-format photographs. These are
// the settings at which that happens.
export const BREAKING_POINTS: string[] = [
	'JPEG down to quality 10',
	'a crop removing 24% on every edge',
	'a 20° rotation',
	'a downscale to 15% of the original size',
	'Gaussian noise at σ = 30/255',
	'25-pixel mean and median filters',
]

export interface Method {
	name: string
	origin: string
	psnrDb: number
	// Exact recovery averaged over the 14 attacks.
	average: number
	bUs: boolean
}

export const METHODS: Method[] = [
	{ name: 'underlayer', origin: '', psnrDb: 45.62, average: 98.5, bUs: true },
	{ name: 'TrustMark-Q', origin: 'Adobe / CAI', psnrDb: 41.98, average: 74.2, bUs: false },
	{ name: 'DWT-STDM', origin: 'classical, Chen & Wornell', psnrDb: 39.13, average: 67.4, bUs: false },
	{ name: 'StegaStamp', origin: 'Tancik et al., CVPR 2020', psnrDb: 29.54, average: 67.3, bUs: false },
	{ name: 'InvisMark', origin: 'Xu et al., WACV 2025', psnrDb: 35.02, average: 66.1, bUs: false },
	{ name: 'TrustMark-P', origin: 'Adobe / CAI', psnrDb: 48.47, average: 42.8, bUs: false },
	{ name: 'SSL', origin: 'Fernandez et al., ICASSP 2022', psnrDb: 42.30, average: 37.3, bUs: false },
]

// Methodology footprint printed under the comparison.
export const ROBUSTNESS_RUN: string[] = [
	'1000 COCO val2017 images, center-cropped and resized to 512×512: identical pixels for every method',
	'The same 96-bit payload for all seven methods, error correction included',
	'14 attacks at one setting each; an identifier counts only if all 96 bits are correct',
	'Robustness report dated 25 August 2026',
]
